import { motion, useMotionValue, animate } from 'motion/react'
import React from 'react'
import styled from 'styled-components'

import { TransactionListItem } from '@root/entities/transaction/ui'

import type { Transaction } from '@root/entities/transaction'

const SLIDE_HEIGHT = 94
const DRAG_THRESHOLD = SLIDE_HEIGHT / 3

type Props = {
  txs: Array<Transaction.Transaction>
}

export const NotificationWidgetTxBody: React.FC<Props> = ({ txs }) => {
  const [index, setIndex] = React.useState(0)
  const y = useMotionValue(0)
  const canDrag = txs.length >= 2

  const goTo = React.useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(txs.length - 1, next))
      setIndex(clamped)
      animate(y, -clamped * SLIDE_HEIGHT, {
        type: 'spring',
        stiffness: 500,
        damping: 40,
      })
    },
    [txs.length, y],
  )

  const handleDragEnd = (
    _: unknown,
    info: { offset: { y: number }; velocity: { y: number } },
  ) => {
    const { offset, velocity } = info
    if (offset.y < -DRAG_THRESHOLD || velocity.y < -500) {
      goTo(index + 1)
    } else if (offset.y > DRAG_THRESHOLD || velocity.y > 500) {
      goTo(index - 1)
    } else {
      goTo(index)
    }
  }

  return (
    <Root data-empty={txs.length === 1}>
      <Viewport>
        <Track
          data-can-drag={canDrag || undefined}
          style={canDrag ? { y } : undefined}
          drag={canDrag ? 'y' : false}
          dragConstraints={
            canDrag
              ? {
                  top: -(txs.length - 1) * SLIDE_HEIGHT,
                  bottom: 0,
                }
              : undefined
          }
          dragElastic={canDrag ? 0.15 : 0}
          onDragEnd={canDrag ? handleDragEnd : undefined}
        >
          {txs.map((tx) => (
            <Slide key={tx.id}>
              <TransactionListItem tx={tx} />
            </Slide>
          ))}
        </Track>
      </Viewport>
      {canDrag ? (
        <Dots>
          {txs.map((tx, i) => (
            <Dot
              key={tx.id}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === index}
              onClick={() => goTo(i)}
            />
          ))}
        </Dots>
      ) : undefined}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
  padding-inline: 4px;

  &[data-empty='true'] {
    grid-template-columns: 1fr;
  }
`

const Viewport = styled.div`
  height: ${SLIDE_HEIGHT}px;
  overflow: hidden;
  touch-action: pan-x;

  --fade: 10px;
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent 0,
    var(--appearance-1) var(--fade),
    var(--appearance-1) calc(100% - var(--fade)),
    transparent 100%
  );
  mask-image: linear-gradient(
    to bottom,
    transparent 0,
    var(--appearance-1) var(--fade),
    var(--appearance-1) calc(100% - var(--fade)),
    transparent 100%
  );
`

const Track = styled(motion.div)`
  display: grid;
  grid-auto-rows: ${SLIDE_HEIGHT}px;

  &[data-can-drag] {
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }
`

const Slide = styled.div`
  display: grid;
  align-items: center;
`

const Dots = styled.div`
  display: grid;
  gap: 3px;
  justify-items: center;
  margin-inline-end: 2px;
`

const Dot = styled.button`
  width: 4px;
  height: 4px;
  padding: 0;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  background: var(--text-11);
  opacity: 0.3;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;

  &[aria-current='true'] {
    opacity: 1;
  }
`
